import { create, useModal } from '@ebay/nice-modal-react';
import Popup from '@shared/components/Popup/Popup';
import { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import { getIsFromGuestServer } from 'modules/Workspaces/WorkspacesProvider/utils';
import { isGuestModeEnabled } from 'utils/utils';

import ScheduleMeetingStep1 from './ScheduleMeetingStep1';
import ScheduleMeetingStep2 from './ScheduleMeetingStep2';
import TwoStepProgressBar from './TwoStepProgressBar';
import { ScheduleMeetingModalProps } from './types';
import useScheduleMeetingForm, { computeIsValid } from './useScheduleMeetingForm';
import useScheduleMeetingSubmit from './useScheduleMeetingSubmit';
import useSlugAvailability from './useSlugAvailability';

const ScheduleMeetingModalInner = ({ messengerRoomId }: ScheduleMeetingModalProps) => {
  const { t } = useTranslation();
  const modal = useModal();
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const { matrixClient } = useMatrixContext();
  const { workspaceId, workspacePermissions } = useWorkspacesContext();
  const isGuestUser = isGuestModeEnabled() && getIsFromGuestServer(matrixClient?.getUserId() ?? '');

  // BUG-001: a workspace user without `invitePeople` cannot create a shared meeting, so the form must
  // start as a team meeting — otherwise the (hidden) toggle stays at its shared default and the invite
  // section presents email-invite affordances for a meeting that will be coerced to team at submit.
  const forceTeamMeeting = !!workspaceId && !workspacePermissions.invitePeople;

  const { form, postfix } = useScheduleMeetingForm({
    messengerRoomId,
    matrixClient,
    defaultValues: { isOrganisationMeeting: forceTeamMeeting },
  });

  const [name, slug, startDate, startTime, endDate, endTime, timezone] = form.watch([
    'name',
    'slug',
    'startDate',
    'startTime',
    'endDate',
    'endTime',
    'timezone',
  ]);

  const slugAvailability = useSlugAvailability(slug, postfix);
  const { isValid, isStartInPast, isEndBeforeStart } = computeIsValid(
    { name, slug, startDate, startTime, endDate, endTime, timezone },
    slugAvailability
  );

  const { handleSubmit, isSubmitting } = useScheduleMeetingSubmit({
    messengerRoomId,
    form,
    setCurrentStep,
  });

  // #8032 Part A — opened from a Messenger chat → single-step simplified flow (no progress bar,
  // no Step 2, submit directly). messengerRoomId is the single discriminator (no extra flag).
  const isMessengerMode = !!messengerRoomId;

  const handleCancel = () => {
    modal.resolve(undefined);
    modal.remove();
  };

  const handleNext = async () => {
    const isFormValid = await form.trigger(['name', 'slug', 'startDate', 'startTime', 'endDate', 'endTime']);
    if (isFormValid && isValid) setCurrentStep(2);
  };

  return (
    <Popup isVisible>
      <Popup.FullScreenContainer className="md:w-[573px]">
        <Popup.Header className="!mx-0 flex-col justify-between px-4 pt-4 !shadow-none !border-none">
          <div className="flex w-full justify-between">
            {t('schedule_meeting.title')}
            <Popup.CloseIcon closePopup={handleCancel} className="!static" />
          </div>
        </Popup.Header>
        {!isMessengerMode && (
          <div className="px-4">
            <TwoStepProgressBar currentStep={currentStep} />
          </div>
        )}
        <FormProvider {...form}>
          {isMessengerMode || currentStep === 1 ? (
            <ScheduleMeetingStep1
              messengerRoomId={messengerRoomId}
              isValid={isValid}
              isStartInPast={isStartInPast}
              isEndBeforeStart={isEndBeforeStart}
              slugAvailability={slugAvailability}
              onNext={isMessengerMode ? handleSubmit : handleNext}
              onCancel={handleCancel}
              isSubmitting={isMessengerMode ? isSubmitting : undefined}
              hideGroupChatToggle={isGuestUser}
            />
          ) : (
            <ScheduleMeetingStep2
              messengerRoomId={messengerRoomId}
              onBack={() => setCurrentStep(1)}
              onCancel={handleCancel}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
            />
          )}
        </FormProvider>
      </Popup.FullScreenContainer>
    </Popup>
  );
};

const ScheduleMeetingModal = create(({ messengerRoomId }: ScheduleMeetingModalProps) => (
  <ScheduleMeetingModalInner messengerRoomId={messengerRoomId} />
));

export default ScheduleMeetingModal;
